import { ccaaLabel, modalityLabel } from './ccaa';
import { extrasAsRows } from './formFields';
import { evidenceExtension, fetchLicenseEvidence, saveBlobAsFile } from './openLicenseEvidence';
import type { LicenseOrderDetail } from './types';
import { buildStoredZip, crc32, storedZipBlob } from '@/utils/storedZip';
import { fileSlug } from '@/lib/file-slug';

export { fileSlug };

export type LicenseEvidenceItem = {
  field: string;
  label: string;
  fileBase: string;
};

export type LicenseDocumentPackSource = Pick<
  LicenseOrderDetail,
  | 'id'
  | 'publicId'
  | 'ccaa'
  | 'modality'
  | 'recipeName'
  | 'duration'
  | 'durationLabel'
  | 'exemption'
  | 'contactEmail'
  | 'contactPhone'
  | 'applicant'
  | 'recipeExtras'
  | 'extraFields'
  | 'documents'
  | 'powerOfAttorney'
>;

const PLACEHOLDER_DNI = '00000000T';

function cleanSheetValue(value: string | null | undefined): string {
  const text = String(value || '').trim();
  if (!text) return '';
  const normalized = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (
    normalized === 'pendiente' ||
    normalized === 'pendiente de datos' ||
    normalized === 'revision' ||
    normalized.startsWith('pendiente ')
  ) {
    return '';
  }
  if (text.toUpperCase() === PLACEHOLDER_DNI) return '';
  if (text.startsWith('1900-01-01')) return '';
  if (text === '00000' || text === '0000') return '';
  return text;
}

function sheetLine(label: string, value: string | null | undefined): string | null {
  const text = cleanSheetValue(value);
  return text ? `${label}: ${text}` : null;
}

export function collectLicenseEvidenceItems(
  detail: LicenseDocumentPackSource
): LicenseEvidenceItem[] {
  const items: LicenseEvidenceItem[] = [];
  if (detail.documents?.identityFront) {
    items.push({
      field: 'identity_front',
      label: 'Identidad anverso',
      fileBase: 'identidad-anverso',
    });
  }
  if (detail.documents?.identityBack) {
    items.push({
      field: 'identity_back',
      label: 'Identidad reverso',
      fileBase: 'identidad-reverso',
    });
  }
  if (detail.powerOfAttorney?.downloadable && detail.powerOfAttorney.label) {
    items.push({
      field: 'power_pdf',
      label: detail.powerOfAttorney.label,
      fileBase: fileSlug(detail.powerOfAttorney.label),
    });
  }
  for (const document of detail.documents?.additional ?? []) {
    items.push({
      field: document.field,
      label: document.label,
      fileBase: fileSlug(document.label),
    });
  }
  const seen = new Set(items.map((item) => item.field));
  for (const row of extrasAsRows(detail.recipeExtras, detail.extraFields)) {
    if (!row.isEvidence || seen.has(row.id)) continue;
    seen.add(row.id);
    items.push({ field: row.id, label: row.label, fileBase: fileSlug(row.label) });
  }
  return items;
}

export function uniqueZipName(used: Set<string>, fileBase: string, extension: string): string {
  const base = fileSlug(fileBase);
  const ext = extension.startsWith('.') || extension === '' ? extension : `.${extension}`;
  let name = `${base}${ext}`;
  let index = 2;
  while (used.has(name)) {
    name = `${base}-${index}${ext}`;
    index += 1;
  }
  used.add(name);
  return name;
}

export function licensePackFileName(detail: LicenseDocumentPackSource): string {
  const who = [detail.applicant.lastName, detail.applicant.secondLastName]
    .map((part) => cleanSheetValue(part))
    .filter(Boolean)
    .map((part) => fileSlug(part))
    .join('-');
  const id = fileSlug(detail.publicId);
  return who ? `${id}-${who}.zip` : `${id}.zip`;
}

export function buildApplicantSheet(detail: LicenseDocumentPackSource): string {
  const applicant = detail.applicant;
  const duration = [detail.durationLabel || detail.duration, detail.exemption ? `(${detail.exemption})` : '']
    .filter(Boolean)
    .join(' ');
  const address = [applicant.addressLine1, applicant.addressLine2].filter(Boolean).join(', ');
  const lines = [
    sheetLine('Pedido', detail.publicId),
    sheetLine('Producto', detail.recipeName),
    sheetLine('Comunidad', ccaaLabel(detail.ccaa)),
    sheetLine('Modalidad', modalityLabel(detail.modality)),
    sheetLine('Duración', duration),
    '',
    sheetLine('Nombre', applicant.firstName),
    sheetLine('Primer apellido', applicant.lastName),
    sheetLine('Segundo apellido', applicant.secondLastName),
    sheetLine('DNI/NIE', applicant.dni),
    sheetLine('Nacimiento', applicant.birthDate?.slice(0, 10)),
    sheetLine('IDESP', applicant.idesp),
    sheetLine('Dirección', address),
    sheetLine('Código postal', applicant.postalCode),
    sheetLine('Localidad', applicant.city),
    sheetLine('Provincia', applicant.province),
    sheetLine('País', applicant.country),
    sheetLine('Email', detail.contactEmail),
    sheetLine('Teléfono', detail.contactPhone),
  ].filter((line): line is string => line !== null);

  const extras = extrasAsRows(detail.recipeExtras, detail.extraFields).filter(
    (row) => !row.isEvidence && row.value && row.value !== 'Sin archivo'
  );
  if (extras.length > 0) {
    lines.push('', 'Campos extra:');
    for (const row of extras) {
      const text = cleanSheetValue(row.value);
      if (text) lines.push(`${row.label}: ${text}`);
    }
  }
  lines.push('');
  return `\uFEFF${lines.join('\n')}`;
}

type EvidenceFetcher = (
  orderId: string,
  field: string
) => Promise<{ blob: Blob; contentType: string }>;

export async function assembleLicenseDocumentPack(
  detail: LicenseDocumentPackSource,
  fetchEvidence: EvidenceFetcher = fetchLicenseEvidence
): Promise<{ filename: string; bytes: Uint8Array; failures: string[] }> {
  const used = new Set<string>(['datos-solicitante.txt', 'errores.txt']);
  const files: Array<{ name: string; data: Uint8Array }> = [
    {
      name: 'datos-solicitante.txt',
      data: new TextEncoder().encode(buildApplicantSheet(detail)),
    },
  ];
  const failures: string[] = [];
  const seenBytes = new Set<string>();

  for (const item of collectLicenseEvidenceItems(detail)) {
    try {
      const result = await fetchEvidence(detail.id, item.field);
      const data = new Uint8Array(await result.blob.arrayBuffer());
      const fingerprint = `${data.length}:${crc32(data)}`;
      if (seenBytes.has(fingerprint)) continue;
      seenBytes.add(fingerprint);
      const name = uniqueZipName(used, item.fileBase, evidenceExtension(result.contentType));
      files.push({ name, data });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo abrir el documento';
      failures.push(`${item.label}: ${message}`);
    }
  }

  if (failures.length > 0) {
    files.push({
      name: 'errores.txt',
      data: new TextEncoder().encode(`${failures.join('\n')}\n`),
    });
  }

  return {
    filename: licensePackFileName(detail),
    bytes: buildStoredZip(files),
    failures,
  };
}

export async function downloadLicenseDocumentPack(detail: LicenseOrderDetail): Promise<void> {
  const pack = await assembleLicenseDocumentPack(detail);
  saveBlobAsFile(storedZipBlob(pack.bytes), pack.filename);
  if (pack.failures.length > 0) {
    throw new Error(
      `Se ha descargado el paquete, pero no se pudo incluir: ${pack.failures.join('; ')}`
    );
  }
}
